import { useState, useCallback, useRef, useEffect } from 'react';
import { AxiosError, type AxiosRequestConfig } from 'axios';
import api from '../services/api';

interface ApiState<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
}

interface UseApiReturn<T> extends ApiState<T> {
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

type ApiFunction<T> = (...args: any[]) => Promise<T>;

export function useApi<T>(apiFn: ApiFunction<T>): UseApiReturn<T> {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    error: null,
    isLoading: false,
  });

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setState({ data: null, error: null, isLoading: true });
      try {
        const result = await apiFn(...args);
        if (mountedRef.current) {
          setState({ data: result, error: null, isLoading: false });
        }
        return result;
      } catch (err) {
        const message =
          err instanceof AxiosError
            ? err.response?.data?.message || err.message
            : 'Something went wrong';
        if (mountedRef.current) {
          setState({ data: null, error: message, isLoading: false });
        }
        return null;
      }
    },
    [apiFn],
  );

  const reset = useCallback(() => {
    setState({ data: null, error: null, isLoading: false });
  }, []);

  return { ...state, execute, reset };
}

export function useApiLazy<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  config?: AxiosRequestConfig,
): UseApiReturn<T> {
  const apiFn = useCallback(
    async (data?: any) => {
      const response =
        method === 'get' || method === 'delete'
          ? await api[method]<{ data: T }>(url, config)
          : await api[method]<{ data: T }>(url, data, config);
      return response.data.data as T;
    },
    [method, url, config],
  );

  return useApi<T>(apiFn);
}

export default useApi;